import BoardView from 'assets/icons/BoardView.svg';
import ListView from 'assets/icons/ListView.svg';
import PortfolioOpportunities from 'assets/icons/portfolioOpportunities.svg';
import { useStore } from 'hooks/useStore';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import { Nullable } from 'primereact/ts-helpers';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { bdportalScreenServices } from 'services/bdportalScreenServices';

const OpportunityFilterLogic = (
  boardType: string,
  viewType: 'Board' | 'List' | 'PortfolioOpportunities',
  setBoardType: (boardType: string) => void
) => {
  const navigate = useNavigate();
  const {
    BDPortalStore: {
      dropdownDetails,
      searchKeyword,
      filterAnalyst,
      filterAccManagers,
      filterStage,
      filterStatus,
      filterSource,
      filterAgency,
      filterPrimarySupplier,
      filterIsPortfolioOpportunity,
      setSearchKeyword,
      setFilterStage,
      setFilterStatus,
      handleClearFilters,
      setFilterAnalyst,
      setFilterAccManagers,
      setFilterSource,
      setFilterAgency,
      setFilterPrimarySupplier,
      filterStartAndEnddates,
      setFilterStartAndEndDate,
      filterAnnualRevenueRange,
      setFilterEstimatedRevenueRange,
      setIsFilterPortfolioOpportunity,
    },
  } = useStore();
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isNewOpportunityModalvisible, setIsNewOpportunityModalVisible] = useState(false);
  const [isPortfolioOpportunitiesModalVisible, setIsPortfolioOpportunitiesModalVisible] =
    useState(false);
  const [isMoleculeSearchInputFocussed, setIsMoleculeSerachInputFocussed] = useState(false);
  const [isAdvancedFiltersVisible, setIsAdvancedFiltersVisible] = useState(false);

  const options = [
    {
      name: 'Board',
      icon: BoardView,
    },
    {
      name: 'List',
      icon: ListView,
    },
    {
      name: 'PortfolioOpportunities',
      icon: PortfolioOpportunities,
    },
  ];

  const handleClickFilter = () => {
    if (isFilterVisible) {
      handleClearFilters();
    }
    setIsFilterVisible(!isFilterVisible);
  };

  const handleAnalytisSelect = (e: MultiSelectChangeEvent) => {
    setFilterAnalyst(e.value);
  };

  const handleAccManagersSelect = (e: MultiSelectChangeEvent) => {
    setFilterAccManagers(e.value);
  };

  const handleSourceSelect = (e: MultiSelectChangeEvent) => {
    setFilterSource(e.value);
  };

  const handleAgencySelect = (e: MultiSelectChangeEvent) => {
    setFilterAgency(e.value);
  };

  const handlePrimarySupplierSelect = (supplier: string) => {
    setFilterPrimarySupplier(supplier);
  };

  const handleIsPortfoliOpportunityOptionSelect = (value: string | null) => {
    if (value === null || value === undefined) {
      setIsFilterPortfolioOpportunity(null);
      return;
    }

    if (value.toLocaleLowerCase() === 'yes') {
      setIsFilterPortfolioOpportunity(true);
    } else if (value.toLocaleLowerCase() === 'no') {
      setIsFilterPortfolioOpportunity(false);
    } else {
      setIsFilterPortfolioOpportunity(null);
    }
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  interface Option {
    label: string;
    value: string;
  }

  const fetchSuggestionsWithDelayForSuppliers = async (inputValue: string): Promise<Option[]> => {
    // Cancel any ongoing request if there is one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort the previous request
    }

    // Create a new AbortController instance for the new request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const response = await bdportalScreenServices.getSuppliersSuggestions(
            inputValue,
            controller.signal
          );

          const suggestions: Option[] = [];
          response.forEach(item => {
            suggestions.push({
              label: item.SupplierName,
              value: item.SupplierName,
            });
          });
          resolve(suggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          resolve([]); // Resolve with an empty array in case of an error
        }
      }, 500); // Delay of 500ms
    });
  };
  const handleViewChange = (view: string) => {
    if (view === 'Board') {
      navigate('/bdportal/optracker/board');
    } else if (view === 'List') {
      navigate('/bdportal/optracker/list');
    } else {
      navigate('/bdportal/optracker/portfolio-opportunities');
    }
  };

  const handleOpenNewOpportunityModal = () => {
    setIsNewOpportunityModalVisible(true);
  };

  const handleCloseNewOpportunityModal = () => {
    setIsNewOpportunityModalVisible(false);
  };

  const handleOpenPortfolioOpportunitiesModal = () => {
    setIsPortfolioOpportunitiesModalVisible(true);
  };

  const handleClosePortfolioOpportunitiesModal = () => {
    setIsPortfolioOpportunitiesModalVisible(false);
  };

  const handleFilterStartAneEndDatesChange = (dates: Nullable<(Date | null)[]>) => {
    setFilterStartAndEndDate(dates);
  };

  const handleIterationTypeChange = (iterationType: string) => {
    handleClearFilters();
    setBoardType(iterationType);
  };

  const handleMoleculeSearchInputFocus = () => {
    setIsMoleculeSerachInputFocussed(true);
  };

  const handleMoleculeSearchInputFocusOut = () => {
    setIsMoleculeSerachInputFocussed(false);
  };

  const handleFilterEstimatedAnnualRevenue = (revenueRanges: [number, number]) => {
    const min = Math.min(revenueRanges[0], revenueRanges[1]);
    const max = Math.max(revenueRanges[0], revenueRanges[1]);
    setFilterEstimatedRevenueRange([min, max]);
  };

  const handleToggleAdvancedFilters = () => {
    setIsAdvancedFiltersVisible(!isAdvancedFiltersVisible);
  };

  useEffect(() => {
    // Store reference to handleClearFilters for cleanup
    const clearFilters = handleClearFilters;
    return () => {
      clearFilters();
    };
  }, [handleClearFilters]);

  return {
    isFilterVisible,
    handleClickFilter,
    handleViewChange,
    options,
    handleOpenNewOpportunityModal,
    handleCloseNewOpportunityModal,
    dropdownDetails,
    isNewOpportunityModalvisible,
    searchKeyword,
    filterAnalyst,
    filterAccManagers,
    filterStage,
    filterStatus,
    handleIterationTypeChange,
    setSearchKeyword,
    setFilterStage,
    setFilterStatus,
    handleAnalytisSelect,
    handleAccManagersSelect,
    handleClearFilters,
    isPortfolioOpportunitiesModalVisible,
    handleOpenPortfolioOpportunitiesModal,
    handleClosePortfolioOpportunitiesModal,
    handleSourceSelect,
    handleAgencySelect,
    handlePrimarySupplierSelect,
    filterSource,
    filterAgency,
    filterPrimarySupplier,
    filterIsPortfolioOpportunity,
    fetchSuggestionsWithDelayForSuppliers,
    handleMoleculeSearchInputFocus,
    handleMoleculeSearchInputFocusOut,
    isMoleculeSearchInputFocussed,
    handleFilterStartAneEndDatesChange,
    filterStartAndEnddates,
    handleFilterEstimatedAnnualRevenue,
    filterAnnualRevenueRange,
    handleIsPortfoliOpportunityOptionSelect,
    handleToggleAdvancedFilters,
    isAdvancedFiltersVisible,
  };
};

export default OpportunityFilterLogic;
